import React from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { NeoButton } from "../../shared/components";

export interface TechnicianChecklistItem {
  id: string;
  title: string;
  instructions?: string;
  required: boolean;
}

interface TechnicianChecklistModalProps {
  open: boolean;
  initialItems: TechnicianChecklistItem[];
  onClose: () => void;
  onSave: (items: TechnicianChecklistItem[]) => void;
}

const newItem = (): TechnicianChecklistItem => ({
  id: `task_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  title: "",
  instructions: "",
  required: true,
});

const TechnicianChecklistModal: React.FC<TechnicianChecklistModalProps> = ({
  open,
  initialItems,
  onClose,
  onSave,
}) => {
  const [items, setItems] = React.useState<TechnicianChecklistItem[]>([]);

  React.useEffect(() => {
    if (open) {
      setItems(initialItems.length > 0 ? initialItems.map((item) => ({ ...item })) : [newItem()]);
    }
  }, [open, initialItems]);

  const updateItem = (index: number, patch: Partial<TechnicianChecklistItem>) => {
    setItems((previous) =>
      previous.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item))
    );
  };

  const validItems = items
    .map((item) => ({ ...item, title: item.title.trim(), instructions: item.instructions?.trim() || "" }))
    .filter((item) => item.title);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 600 }}>
        Configure Technician checklist
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ color: "#666", fontFamily: "var(--theo-font-sans)", mb: 2 }}>
          Add tasks Theo cannot perform. The workflow pauses until every required task is completed,
          skipped, or marked blocked by a technician.
        </Typography>
        {items.map((item, index) => (
          <Box
            key={item.id}
            sx={{ border: "1px solid #E5E7EB", borderRadius: 1.5, p: 2, mb: 1.5 }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
              <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 600, pt: 1 }}>
                {index + 1}.
              </Typography>
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1.5 }}>
                <TextField
                  label="Task"
                  placeholder="e.g. Create user on server"
                  value={item.title}
                  onChange={(event) => updateItem(index, { title: event.target.value })}
                  required
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Instructions or completion criteria"
                  placeholder="Optional details for the technician"
                  value={item.instructions || ""}
                  onChange={(event) => updateItem(index, { instructions: event.target.value })}
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={item.required}
                      onChange={(event) => updateItem(index, { required: event.target.checked })}
                    />
                  }
                  label="Required before the workflow can continue"
                />
              </Box>
              <IconButton
                aria-label="Remove technician task"
                onClick={() => setItems((previous) => previous.filter((_, itemIndex) => itemIndex !== index))}
                disabled={items.length === 1}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Box>
          </Box>
        ))}
        <Button
          startIcon={<AddIcon />}
          onClick={() => setItems((previous) => [...previous, newItem()])}
          sx={{ textTransform: "none", fontFamily: "var(--theo-font-sans)" }}
        >
          Add task
        </Button>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <NeoButton variant="neo" onClick={() => onSave(validItems)} disabled={validItems.length === 0}>
          Save checklist
        </NeoButton>
      </DialogActions>
    </Dialog>
  );
};

export default TechnicianChecklistModal;
